import {
  Box,
  Center,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
} from '@chakra-ui/react'
import { BloodPressure } from '@hb/shared'
import { useEffect, useRef, useState } from 'react'
import { useField } from 'react-final-form'
import { VitalsSlider } from './VitalsSlider'

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Status Helpers ───────────────────────────────────────────────────────────

const getTempStatus = (temp: number): { text: string; color: string } => {
  if (temp < 96.0) return { text: 'Hypothermia', color: '#4299e1' }
  if (temp < 97.5) return { text: 'Low', color: '#63b3ed' }
  if (temp <= 99.5) return { text: 'Normal', color: '#48bb78' }
  if (temp <= 100.9) return { text: 'Low Fever', color: '#f6ad55' }
  if (temp <= 103.0) return { text: 'Fever', color: '#fc8181' }
  return { text: 'High Fever', color: '#e53e3e' }
}

/**
 * Obstetric BP thresholds:
 * Severe HTN ≥ 160/110 · Mild HTN 140–159 / 90–109 · Normal < 140/90 · Low < 90/60
 */
const getBpStatus = (systolic: number, diastolic: number): { text: string; color: string } => {
  if (systolic > 180 || diastolic > 120) return { text: 'Hypertensive Crisis', color: '#e53e3e' }
  if (systolic >= 160 || diastolic >= 110) return { text: 'Severe HTN', color: '#e53e3e' }
  if (systolic >= 140 || diastolic >= 90) return { text: 'Mild HTN', color: '#f6ad55' }
  if (systolic < 90 || diastolic < 60) return { text: 'Low BP', color: '#63b3ed' }
  if (systolic >= 120 && diastolic < 80) return { text: 'Elevated', color: '#ecc94b' }
  return { text: 'Normal', color: '#48bb78' }
}

const getPulseStatus = (bpm: number): { text: string; color: string } => {
  if (bpm < 50) return { text: 'Severe Bradycardia', color: '#4299e1' }
  if (bpm < 60) return { text: 'Bradycardia', color: '#63b3ed' }
  if (bpm <= 100) return { text: 'Normal', color: '#48bb78' }
  if (bpm <= 120) return { text: 'Tachycardia', color: '#f6ad55' }
  return { text: 'Severe Tachycardia', color: '#e53e3e' }
}

const getRRStatus = (rr: number): { text: string; color: string } => {
  if (rr <= 0) return { text: '–', color: '#a0aec0' }
  if (rr < 12) return { text: 'Bradypnea', color: '#63b3ed' }
  if (rr <= 20) return { text: 'Normal', color: '#48bb78' }
  if (rr <= 30) return { text: 'Tachypnea', color: '#f6ad55' }
  return { text: 'Severe Tachypnea', color: '#e53e3e' }
}

// ─── Thermometer Visual ───────────────────────────────────────────────────────

/** SVG thermometer — stem fills from bottom as temp rises from 95 → 106 °F */
const ThermometerViz = ({ temp, color }: { temp: number; color: string }) => {
  const STEM_TOP = 10
  const STEM_BOTTOM = 88
  const STEM_HEIGHT = STEM_BOTTOM - STEM_TOP
  const pct = Math.max(0, Math.min(1, (temp - 95) / 11))
  const fillY = STEM_BOTTOM - pct * STEM_HEIGHT

  return (
    <Box w="36px" h="120px" flexShrink={0}>
      <svg width="36" height="120" viewBox="0 0 36 120">
        {/* Bulb outline */}
        <circle cx="18" cy="104" r="13" fill="none" stroke="#CBD5E0" strokeWidth="2" />
        {/* Stem outline */}
        <rect
          x="12"
          y={STEM_TOP}
          width="12"
          height={STEM_HEIGHT + 6}
          rx="6"
          fill="none"
          stroke="#CBD5E0"
          strokeWidth="2"
        />
        {/* Stem fill */}
        <rect
          x="14"
          y={fillY}
          width="8"
          height={STEM_BOTTOM - fillY + 6}
          rx="3"
          fill={color}
          opacity={0.85}
        />
        {/* Bulb fill */}
        <circle cx="18" cy="104" r="10" fill={color} opacity={0.85} />
        {/* Tick marks */}
        {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
          <line
            key={i}
            x1="9"
            y1={STEM_BOTTOM - t * STEM_HEIGHT}
            x2="12"
            y2={STEM_BOTTOM - t * STEM_HEIGHT}
            stroke="#A0AEC0"
            strokeWidth="1.5"
          />
        ))}
      </svg>
    </Box>
  )
}

// ─── Range Reference Row ──────────────────────────────────────────────────────

const RangeRef = ({ items }: { items: { label: string; value: string; color: string }[] }) => (
  <Flex gap={3} wrap="wrap" align="center" pt={1}>
    {items.map(({ label, value, color }) => (
      <Flex key={label} align="baseline" gap={1}>
        <Text fontSize="xs" color="gray.400" fontFamily="hero-new">
          {label}:
        </Text>
        <Text fontSize="xs" fontWeight={600} color={color} fontFamily="hero-new">
          {value}
        </Text>
      </Flex>
    ))}
  </Flex>
)

// ─── Temperature ──────────────────────────────────────────────────────────────

const temperatureGradient = 'linear-gradient(to right,rgb(30, 81, 184),rgb(255, 40, 40))'
const darkModeTemperatureGradient = 'linear-gradient(to right,rgb(70, 128, 243),rgb(255, 40, 40))'

const TemperatureInput = () => {
  const { input } = useField('temperature', { subscription: { value: true } })
  const inputRef = useRef<HTMLInputElement>(null)
  const [tempValue, setTempValue] = useState(
    typeof input.value === 'number' ? input.value.toFixed(1) : '',
  )
  useEffect(() => {
    setTempValue(typeof input.value === 'number' ? input.value.toFixed(1) : '')
  }, [input.value])

  const numVal = typeof input.value === 'number' ? input.value : null
  const { text: statusText, color: statusColor } =
    numVal !== null ? getTempStatus(numVal) : { text: '–', color: '#a0aec0' }

  return (
    <Flex flexFlow="column" w="100%" gap={3} py={2}>
      <Flex justify="space-between" align="center">
        <SectionLabel>TEMPERATURE</SectionLabel>
        <Flex align="baseline" gap={2}>
          {numVal !== null && (
            <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color={statusColor}>
              {numVal.toFixed(1)} °F
            </Text>
          )}
          <Text fontSize="sm" fontWeight={600} fontFamily="hero-new" color={statusColor}>
            {statusText}
          </Text>
        </Flex>
      </Flex>
      <Flex align="center" gap={4} w="100%">
        <ThermometerViz temp={numVal ?? 98.6} color={statusColor} />
        <Flex flexFlow="column" flex={1} gap={3} minW={0}>
          <InputGroup w="140px">
            <Input
              onBlur={e => {
                const asNumber = Number(e.target.value)
                if (isNaN(asNumber)) return
                input.onChange(Math.max(95, Math.min(106, asNumber)))
              }}
              onChange={e => setTempValue(e.target.value)}
              bg="white"
              _dark={{ bg: '#2a2a2a' }}
              ref={inputRef}
              value={tempValue}
              px={2}
              fontFamily="hero-new"
              fontSize="xl"
              type="number"
              placeholder="98.6"
            />
            <InputRightAddon
              px={2.5}
              fontWeight={500}
              _dark={{ bg: '#4a4a4a' }}
              fontSize="xl"
              fontFamily="hero-new"
              pointerEvents="none">
              °F
            </InputRightAddon>
          </InputGroup>
          <Box pb={6}>
            <VitalsSlider
              value={typeof input.value === 'number' ? input.value : undefined}
              min={95}
              max={106}
              step={0.1}
              trackGradient={temperatureGradient}
              trackGradientDark={darkModeTemperatureGradient}
              formatValue={v => v.toFixed(1)}
              onDragValue={s => {
                if (!inputRef.current) return
                inputRef.current.value = s
              }}
              onChangeCommitted={input.onChange}
            />
          </Box>
        </Flex>
      </Flex>
      <RangeRef
        items={[
          { label: 'Normal', value: '97.5–99.5 °F', color: '#48bb78' },
          { label: 'Low Fever', value: '99.5–100.9', color: '#f6ad55' },
          { label: 'Fever', value: '≥101 °F', color: '#fc8181' },
        ]}
      />
    </Flex>
  )
}

// ─── Blood Pressure ───────────────────────────────────────────────────────────

const systolicGradient = 'linear-gradient(to right,rgb(72, 99, 160),rgb(200, 48, 48))'
const darkModeSystolicGradient = 'linear-gradient(to right,rgb(100, 140, 220),rgb(255, 100, 100))'
const diastolicGradient = 'linear-gradient(to right,rgb(60, 120, 140),rgb(90, 160, 200))'
const darkModeDiastolicGradient = 'linear-gradient(to right,rgb(80, 150, 170),rgb(120, 190, 230))'

const BloodPressureInput = () => {
  const { input } = useField('bloodPressure', { subscription: { value: true } })

  const readBp = (): BloodPressure => {
    const v = input.value as Partial<BloodPressure> | undefined
    return {
      systolic: typeof v?.systolic === 'number' ? v.systolic : 120,
      diastolic: typeof v?.diastolic === 'number' ? v.diastolic : 80,
    }
  }

  const sysInputRef = useRef<HTMLInputElement>(null)
  const diaInputRef = useRef<HTMLInputElement>(null)
  const bp = readBp()
  const [sysValue, setSysValue] = useState(String(bp.systolic))
  const [diaValue, setDiaValue] = useState(String(bp.diastolic))

  useEffect(() => {
    const v = input.value as Partial<BloodPressure> | undefined
    const systolic = typeof v?.systolic === 'number' ? v.systolic : 120
    const diastolic = typeof v?.diastolic === 'number' ? v.diastolic : 80
    setSysValue(String(systolic))
    setDiaValue(String(diastolic))
  }, [input.value])

  const { text: bpStatus, color: bpColor } = getBpStatus(bp.systolic, bp.diastolic)

  const inputProps = {
    bg: 'white' as const,
    _dark: { bg: '#2a2a2a' as const },
    px: 2,
    fontFamily: 'hero-new' as const,
    fontSize: 'xl' as const,
    type: 'number' as const,
  }
  const addonProps = {
    px: 2.5,
    fontWeight: 500,
    _dark: { bg: '#4a4a4a' },
    fontSize: 'xl' as const,
    fontFamily: 'hero-new' as const,
    pointerEvents: 'none' as const,
  }

  return (
    <Flex flexFlow="column" w="100%" gap={3} py={2}>
      <Flex justify="space-between" align="center">
        <SectionLabel>BLOOD PRESSURE</SectionLabel>
        <Flex align="baseline" gap={2}>
          <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color={bpColor}>
            {bp.systolic}/{bp.diastolic}
          </Text>
          <Text fontSize="sm" fontWeight={600} fontFamily="hero-new" color={bpColor}>
            {bpStatus}
          </Text>
        </Flex>
      </Flex>

      {/* Classification bar */}
      <Flex
        w="100%"
        flexFlow="column"
        gap={1.5}
        bg="white"
        borderRadius={12}
        border="1px solid"
        borderColor="gray.200"
        _dark={{ bg: '#2a2a2a', borderColor: 'gray.600' }}
        px={4}
        py={3}>
        <Flex justify="space-between" align="center">
          <Text
            fontSize="xs"
            fontFamily="hero-new"
            color="gray.500"
            _dark={{ color: 'gray.300' }}
            fontWeight={500}
            letterSpacing="wider">
            CLASSIFICATION
          </Text>
          <Text fontSize="sm" fontWeight={700} fontFamily="hero-new" color={bpColor}>
            {bpStatus}
          </Text>
        </Flex>
        {/* 5-zone bar */}
        <Flex w="100%" h="8px" borderRadius="full" overflow="hidden" gap="2px">
          {(
            [
              { color: '#63b3ed', flex: 1 },
              { color: '#48bb78', flex: 2 },
              { color: '#ecc94b', flex: 1 },
              { color: '#f6ad55', flex: 1 },
              { color: '#e53e3e', flex: 1.5 },
            ] as const
          ).map((z, i) => (
            <Box key={i} flex={z.flex} h="100%" bg={z.color} borderRadius="2px" />
          ))}
        </Flex>
        <Flex justify="space-between">
          {['Low', 'Normal', 'Elevated', 'Mild HTN', 'Severe HTN'].map((label, i) => (
            <Text
              key={label}
              fontSize="9px"
              fontFamily="hero-new"
              color={['#63b3ed', '#48bb78', '#ecc94b', '#f6ad55', '#e53e3e'][i]}
              fontWeight={bpStatus === label ? 700 : 400}
              flex={[1, 2, 1, 1, 1.5][i]}>
              {label}
            </Text>
          ))}
        </Flex>
      </Flex>

      <Flex flexFlow="column" gap={4} w="100%">
        <Flex gap={4} w="100%" align="center">
          <InputGroup w="130px">
            <Input
              {...inputProps}
              onBlur={e => {
                const asNumber = Number(e.target.value)
                if (isNaN(asNumber)) return
                const clamped = Math.max(60, Math.min(220, asNumber))
                const cur = readBp()
                input.onChange({ ...cur, systolic: clamped })
              }}
              onChange={e => setSysValue(e.target.value)}
              ref={sysInputRef}
              value={sysValue}
              placeholder="120"
            />
            <InputRightAddon {...addonProps}>SYS</InputRightAddon>
          </InputGroup>
          <VitalsSlider
            value={typeof input.value.systolic === 'number' ? input.value.systolic : undefined}
            min={60}
            max={220}
            step={1}
            trackGradient={systolicGradient}
            trackGradientDark={darkModeSystolicGradient}
            formatValue={v => String(v)}
            onDragValue={s => {
              if (!sysInputRef.current) return
              sysInputRef.current.value = s
            }}
            onChangeCommitted={value => {
              const cur = readBp()
              input.onChange({ ...cur, systolic: value })
            }}
          />
        </Flex>
        <Flex gap={4} w="100%" align="center">
          <InputGroup w="130px">
            <Input
              {...inputProps}
              onBlur={e => {
                const asNumber = Number(e.target.value)
                if (isNaN(asNumber)) return
                const clamped = Math.max(40, Math.min(140, asNumber))
                const cur = readBp()
                input.onChange({ ...cur, diastolic: clamped })
              }}
              onChange={e => setDiaValue(e.target.value)}
              ref={diaInputRef}
              value={diaValue}
              placeholder="80"
            />
            <InputRightAddon {...addonProps}>DIA</InputRightAddon>
          </InputGroup>
          <VitalsSlider
            value={typeof input.value.diastolic === 'number' ? input.value.diastolic : undefined}
            min={40}
            max={140}
            step={1}
            trackGradient={diastolicGradient}
            trackGradientDark={darkModeDiastolicGradient}
            formatValue={v => String(v)}
            onDragValue={s => {
              if (!diaInputRef.current) return
              diaInputRef.current.value = s
            }}
            onChangeCommitted={value => {
              const cur = readBp()
              input.onChange({ ...cur, diastolic: value })
            }}
          />
        </Flex>
      </Flex>
      <RangeRef
        items={[
          { label: 'Normal', value: '< 140/90', color: '#48bb78' },
          { label: 'Mild HTN', value: '140–159 / 90–109', color: '#f6ad55' },
          { label: 'Severe', value: '≥ 160/110', color: '#e53e3e' },
        ]}
      />
    </Flex>
  )
}

// ─── Pulse ────────────────────────────────────────────────────────────────────

const pulseGradient = 'linear-gradient(to right,rgb(80, 55, 130),rgb(220, 70, 110))'
const darkModePulseGradient = 'linear-gradient(to right,rgb(120, 95, 200),rgb(255, 120, 150))'

const PULSE_MIN = 30
const PULSE_MAX = 180

/** Simple SVG pulse-wave mark */
const PulseWave = ({ color }: { color: string }) => (
  <Box w="64px" h="40px" flexShrink={0}>
    <svg width="64" height="40" viewBox="0 0 64 40">
      <polyline
        points="0,20 12,20 18,6 24,34 30,20 36,20 44,4 50,36 56,20 64,20"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.85}
      />
    </svg>
  </Box>
)

const PulseInput = () => {
  const { input } = useField('pulse', { subscription: { value: true } })
  const inputRef = useRef<HTMLInputElement>(null)
  const [pulseValue, setPulseValue] = useState(
    typeof input.value === 'number' ? String(Math.round(input.value)) : '',
  )
  useEffect(() => {
    setPulseValue(typeof input.value === 'number' ? String(Math.round(input.value)) : '')
  }, [input.value])

  const numVal = typeof input.value === 'number' ? input.value : null
  const { text: statusText, color: statusColor } =
    numVal !== null ? getPulseStatus(numVal) : { text: '–', color: '#a0aec0' }

  return (
    <Flex flexFlow="column" w="100%" gap={3} py={2}>
      <Flex justify="space-between" align="center">
        <SectionLabel>PULSE</SectionLabel>
        <Flex align="baseline" gap={2}>
          {numVal !== null && (
            <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color={statusColor}>
              {Math.round(numVal)} bpm
            </Text>
          )}
          <Text fontSize="sm" fontWeight={600} fontFamily="hero-new" color={statusColor}>
            {statusText}
          </Text>
        </Flex>
      </Flex>
      <Flex align="center" gap={4} w="100%">
        <PulseWave color={statusColor} />
        <Flex flexFlow="column" flex={1} gap={3} minW={0}>
          <InputGroup w="130px">
            <Input
              onBlur={e => {
                const asNumber = Number(e.target.value)
                if (isNaN(asNumber)) return
                input.onChange(Math.max(PULSE_MIN, Math.min(PULSE_MAX, Math.round(asNumber))))
              }}
              onChange={e => setPulseValue(e.target.value)}
              bg="white"
              _dark={{ bg: '#2a2a2a' }}
              ref={inputRef}
              value={pulseValue}
              px={2}
              fontFamily="hero-new"
              fontSize="xl"
              type="number"
              placeholder="70"
            />
            <InputRightAddon
              px={2.5}
              fontWeight={500}
              _dark={{ bg: '#4a4a4a' }}
              fontSize="xl"
              fontFamily="hero-new"
              pointerEvents="none">
              bpm
            </InputRightAddon>
          </InputGroup>
          <Box pb={6}>
            <VitalsSlider
              value={typeof input.value === 'number' ? input.value : undefined}
              min={PULSE_MIN}
              max={PULSE_MAX}
              step={1}
              trackGradient={pulseGradient}
              trackGradientDark={darkModePulseGradient}
              formatValue={v => String(Math.round(v))}
              onDragValue={s => {
                if (!inputRef.current) return
                inputRef.current.value = s
              }}
              onChangeCommitted={v => input.onChange(Math.round(v))}
            />
          </Box>
        </Flex>
      </Flex>
      <RangeRef
        items={[
          { label: 'Bradycardia', value: '< 60 bpm', color: '#63b3ed' },
          { label: 'Normal', value: '60–100 bpm', color: '#48bb78' },
          { label: 'Tachycardia', value: '> 100 bpm', color: '#f6ad55' },
        ]}
      />
    </Flex>
  )
}

// ─── Respiratory Rate ─────────────────────────────────────────────────────────

const respiratoryRateGradient = 'linear-gradient(to right,rgb(40, 120, 110),rgb(100, 200, 190))'
const darkModeRespiratoryRateGradient =
  'linear-gradient(to right,rgb(70, 160, 150),rgb(130, 220, 210))'

const RR_MIN = 0
const RR_MAX = 60

/** Simplified breath-wave SVG */
const BreathWave = ({ color }: { color: string }) => (
  <Box w="64px" h="40px" flexShrink={0}>
    <svg width="64" height="40" viewBox="0 0 64 40">
      <path
        d="M0,20 Q8,20 16,8 Q24,20 32,20 Q40,20 48,8 Q56,20 64,20"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity={0.85}
      />
    </svg>
  </Box>
)

const RespiratoryRateInput = () => {
  const { input } = useField('respiratoryRate', { subscription: { value: true } })
  const inputRef = useRef<HTMLInputElement>(null)
  const [rrValue, setRrValue] = useState(
    typeof input.value === 'number' ? String(Math.round(input.value)) : '',
  )
  useEffect(() => {
    setRrValue(typeof input.value === 'number' ? String(Math.round(input.value)) : '')
  }, [input.value])

  const numVal = typeof input.value === 'number' ? input.value : null
  const { text: statusText, color: statusColor } =
    numVal !== null ? getRRStatus(numVal) : { text: '–', color: '#a0aec0' }

  return (
    <Flex flexFlow="column" w="100%" gap={3} py={2}>
      <Flex justify="space-between" align="center">
        <SectionLabel>RESPIRATORY RATE</SectionLabel>
        <Flex align="baseline" gap={2}>
          {numVal !== null && (
            <Text fontSize="xl" fontWeight={600} fontFamily="hero-new" color={statusColor}>
              {Math.round(numVal)} br/min
            </Text>
          )}
          <Text fontSize="sm" fontWeight={600} fontFamily="hero-new" color={statusColor}>
            {statusText}
          </Text>
        </Flex>
      </Flex>
      <Flex align="center" gap={4} w="100%">
        <BreathWave color={statusColor} />
        <Flex flexFlow="column" flex={1} gap={3} minW={0}>
          <InputGroup w="130px">
            <Input
              onBlur={e => {
                const asNumber = Number(e.target.value)
                if (isNaN(asNumber)) return
                input.onChange(Math.max(RR_MIN, Math.min(RR_MAX, Math.round(asNumber))))
              }}
              onChange={e => setRrValue(e.target.value)}
              bg="white"
              _dark={{ bg: '#2a2a2a' }}
              ref={inputRef}
              value={rrValue}
              px={2}
              fontFamily="hero-new"
              fontSize="xl"
              type="number"
              placeholder="16"
            />
            <InputRightAddon
              px={2}
              fontWeight={500}
              _dark={{ bg: '#4a4a4a' }}
              fontSize="md"
              fontFamily="hero-new"
              pointerEvents="none">
              br/min
            </InputRightAddon>
          </InputGroup>
          <Box pb={6}>
            <VitalsSlider
              value={typeof input.value === 'number' ? input.value : undefined}
              min={RR_MIN}
              max={RR_MAX}
              step={1}
              trackGradient={respiratoryRateGradient}
              trackGradientDark={darkModeRespiratoryRateGradient}
              formatValue={v => String(Math.round(v))}
              onDragValue={s => {
                if (!inputRef.current) return
                inputRef.current.value = s
              }}
              onChangeCommitted={v => {
                input.onChange(Math.round(v))
              }}
            />
          </Box>
        </Flex>
      </Flex>
      <RangeRef
        items={[
          { label: 'Bradypnea', value: '< 12', color: '#63b3ed' },
          { label: 'Normal', value: '12–20 br/min', color: '#48bb78' },
          { label: 'Tachypnea', value: '> 20', color: '#f6ad55' },
        ]}
      />
    </Flex>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const VitalsEntry = () => (
  <Center pb={8} w="100%">
    <Flex flexFlow="column" w="100%" maxW="520px" gap={0} py={2} px={2} align="center">
      <TemperatureInput />
      <Divider />
      <BloodPressureInput />
      <Divider />
      <PulseInput />
      <Divider />
      <RespiratoryRateInput />
    </Flex>
  </Center>
)
